import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('request'); // 'request' | 'sent'
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [apiError, setApiError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateEmail = () => {
        if (!email.trim()) {
            setEmailError('Email is required');
            return false;
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
            setEmailError('Email is invalid');
            return false;
        }
        setEmailError('');
        return true;
    };

    const handleChange = (e) => {
        setEmail(e.target.value);
        if (emailError) setEmailError('');
        if (apiError) setApiError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateEmail()) return;

        setIsSubmitting(true);
        setApiError('');

        try {
            await api.forgotPassword({ email });
            setStep('sent');
        } catch (err) {
            setApiError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050d1f] text-white flex flex-col items-center p-4 sm:p-8 relative overflow-hidden font-sans">
            {/* bg glows */}
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#4d7cff]/10 rounded-full blur-[120px]" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#4d7cff]/5 rounded-full blur-[100px]" />

            {/* nav */}
            <div className="w-full max-w-7xl flex justify-between items-center mb-6 sm:mb-12 z-10">
                <button onClick={() => navigate('/')} className="text-2xl sm:text-3xl font-bold text-[#4d7cff] tracking-tight">
                    PlayerIQ
                </button>
            </div>

            {/* centered card */}
            <div className="z-10 w-full max-w-md mt-10 sm:mt-20">
                <div className="relative">
                    <div className="absolute -inset-0.5 bg-[#4d7cff]/20 rounded-[32px] blur-sm" />
                    <div className="relative bg-[#0c1526]/95 backdrop-blur-2xl border border-white/[0.08] rounded-[24px] sm:rounded-[32px] p-6 sm:p-10">

                        {step === 'request' ? (
                            <>
                                {/* icon */}
                                <div className="flex justify-center mb-6">
                                    <div className="w-14 h-14 rounded-full bg-[#4d7cff]/15 border border-[#4d7cff]/30 flex items-center justify-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <rect x="3" y="11" width="18" height="11" rx="2" stroke="#4d7cff" strokeWidth="1.8" strokeLinecap="round"/>
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#4d7cff" strokeWidth="1.8" strokeLinecap="round"/>
                                            <circle cx="12" cy="16" r="1.5" fill="#4d7cff"/>
                                        </svg>
                                    </div>
                                </div>

                                <h2 className="text-2xl font-bold text-center mb-2">Forgot Password?</h2>
                                <p className="text-white/40 text-sm text-center mb-8 leading-relaxed">
                                    No worries. Enter your email and we'll send you a reset link.
                                </p>

                                {apiError && (
                                    <div className="mb-6 px-5 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                                        {apiError}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="flex flex-col gap-2">
                                        <input
                                            type="email"
                                            placeholder="Email"
                                            value={email}
                                            onChange={handleChange}
                                            className={`w-full bg-white/10 rounded-full px-7 py-4 text-white outline-none focus:ring-2 focus:ring-[#4d7cff]/30 placeholder:text-white/30 ${
                                                emailError ? 'ring-2 ring-red-500' : ''
                                            }`}
                                        />
                                        {emailError && (
                                            <span className="text-red-500 text-sm pl-5">{emailError}</span>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full bg-[#4d7cff] rounded-full px-7 py-4 text-white text-lg font-bold shadow-[0_8px_24px_rgba(77,124,255,0.3)] hover:bg-[#3d6cef] transition-all disabled:opacity-70"
                                    >
                                        {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </form>

                                <p className="text-center text-white/40 text-sm mt-6">
                                    Remember your password?{' '}
                                    <button
                                        type="button"
                                        onClick={() => navigate('/')}
                                        className="text-[#4d7cff] hover:underline"
                                    >
                                        Back to login
                                    </button>
                                </p>
                            </>
                        ) : (
                            /* ── success / sent state ── */
                            <>
                                <div className="flex justify-center mb-6">
                                    <div className="w-14 h-14 rounded-full bg-[#4d7cff]/15 border border-[#4d7cff]/30 flex items-center justify-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="#4d7cff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                </div>

                                <h2 className="text-2xl font-bold text-center mb-2">Check Your Email</h2>
                                <p className="text-white/40 text-sm text-center mb-2 leading-relaxed">
                                    We sent a password reset link to
                                </p>
                                <p className="text-[#4d7cff] text-sm font-semibold text-center mb-8 break-all">{email}</p>

                                <div className="px-5 py-4 rounded-2xl bg-white/5 border border-white/[0.08] text-white/50 text-sm text-center leading-relaxed mb-8">
                                    Didn't receive it? Check your spam folder or{' '}
                                    <button
                                        type="button"
                                        onClick={() => setStep('request')}
                                        className="text-[#4d7cff] hover:underline"
                                    >
                                        try a different email
                                    </button>
                                    .
                                </div>

                                <button
                                    type="button"
                                    onClick={() => navigate('/')}
                                    className="w-full bg-[#4d7cff] rounded-full px-7 py-4 text-white text-lg font-bold shadow-[0_8px_24px_rgba(77,124,255,0.3)] hover:bg-[#3d6cef] transition-all"
                                >
                                    Back to Login
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;